import { isNull, noop } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { ANALYSIS_MARGIN, NAV_BAR, PAGE_MARGIN_PADDING_BORDER, TIMELINE_WRAPPER, useSplitView } from '../../../../hooks/useSplitView';
import { getAmendmentInformation, getDocumentAnalysisView, getParentDatasetInstanceIsDraft, getSuggestedAndRemainingQueries, setAmendmentConfirmedQueriesStarted, setConfirmedQueries } from '../../../documents/my-documents/store';
import { ButtonResize } from '../../../shared/button/ButtonResize';
import { Robot } from '../../../shared/icons';
import { ModalHeader } from '../../../shared/modal/ModalHeader';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { Toggle } from '../../../shared/toggle';
import { InformationTooltip, OverflowTooltip } from '../../../shared/tooltip';
import { DocumentQuery } from '../../store/mlTypes';
import { DatasetModal } from '../modal-instance/DatasetModal';
import { getAmendmentMLDataModalOpen, toggleAmendmentMLDataModal } from '../store';
import styles from './MLDataModal.module.scss';

const MODAL_HEADER_HEIGHT = 56;
const MODAL_PADDING = 20;
const widthOffset = PAGE_MARGIN_PADDING_BORDER;
const BOTTOM_BUTTON = 50;
const MODAL_INFO = 27;
const SPLIT_VIEW_HEIGHT_OFFSET = NAV_BAR + MODAL_HEADER_HEIGHT + MODAL_PADDING + ANALYSIS_MARGIN;
const MODAL_TOP_OFFSET = NAV_BAR + MODAL_HEADER_HEIGHT + MODAL_PADDING + ANALYSIS_MARGIN + TIMELINE_WRAPPER;
const MODAL_BODY_HEIGHT_OFFSET = BOTTOM_BUTTON + ANALYSIS_MARGIN + MODAL_INFO;

export const AmendmentMLDataModal: React.FC = () => {
    const dispatch = useAppDispatch();

    const isOpen = useAppSelector(getAmendmentMLDataModalOpen);
    const allQueries = useAppSelector(getSuggestedAndRemainingQueries);
    const amendmentInformation = useAppSelector(getAmendmentInformation);
    const { suggestedQueries, remainingQueries } = allQueries;
    const documentAnalysisView = useAppSelector(getDocumentAnalysisView);
    const parentDatasetInstanceIsDraft = useAppSelector(getParentDatasetInstanceIsDraft);
    const { confirmedQueries } = amendmentInformation;

    const [width, height] = useSplitView(documentAnalysisView, widthOffset, SPLIT_VIEW_HEIGHT_OFFSET);

    const toggleQueries = useCallback((query: DocumentQuery) => dispatch(setConfirmedQueries(query)), [dispatch]);
    const confirm = () => dispatch(setAmendmentConfirmedQueriesStarted());

    const disabledConfirmTooltip = useMemo(() => parentDatasetInstanceIsDraft ? ['Previous instance is still in draft'] : [], [parentDatasetInstanceIsDraft]);

    const amendmentModalBody = useMemo(() => (
        <div style={{ maxHeight: `${height - MODAL_BODY_HEIGHT_OFFSET}px` }}>
            <Scrollable maxHeight={`${height - MODAL_BODY_HEIGHT_OFFSET}px`}>
                <div className={styles.mlDatasetTableHeader}>
                    <div className={styles.headerCell} style={{ marginLeft: '10px' }}>Suggested Queries</div>
                </div>
                {suggestedQueries.length === 0 ?
                    <div className={styles.suggestedQueriesEmptyMessage}>AI was not able to detect any suggested queries based on this amendment</div> :
                    suggestedQueries.map((query, index) => (
                        <div key={index} className={styles.questionAndToggleWrapper}>
                            <div>{query.question}</div>
                            <Toggle checked={(!isNull(confirmedQueries) && confirmedQueries.includes(query.key))} onChange={() => toggleQueries(query)} />
                        </div>
                    ))}
                <div className={styles.mlDatasetTableHeader}>
                    <div className={styles.headerCell} style={{ marginLeft: '10px' }}>Remaining Queries</div>
                </div>
                <div className={styles.remainingQueriesNote}>*Please Note: We don't believe the amendment answers these questions.</div>
                {remainingQueries.map((query, index) => (
                    <div key={index} className={styles.questionAndToggleWrapper}>
                        <div>{query.question}</div>
                        <Toggle checked={(!isNull(confirmedQueries) && confirmedQueries.includes(query.key))} onChange={() => toggleQueries(query)} />
                    </div>
                ))}
            </Scrollable>
        </div>
    ), [confirmedQueries, remainingQueries, suggestedQueries, toggleQueries, height]);

    useEffect(() => () => {
        dispatch(toggleAmendmentMLDataModal(false));
    }, [dispatch]);

    return (
        <DatasetModal
            isOpen={isOpen}
            closeModal={noop}
            topOffset={`${MODAL_TOP_OFFSET}px`}
            timelineOffset
        >
            <div className={styles.amendmentDataModalWrapper} data-testid='ml-dataset-wrapper' style={{ width }}>
                <ModalHeader label='AI Data Extracted' icon={Robot} />
                <div className={styles.amendmentDataModalContent}>
                    <OverflowTooltip className={styles.amendmentDataModalTitle} overlayText='These are the questions we believe we can answer using AI to populate the data map with the updated amendment.' />
                    <div className={styles.amendmentDataModalInfoWrapper}>
                        <OverflowTooltip className={styles.amendmentDataModalInfo} overlayText='Please verify, modify and confirm which questions you would like to ask.' />
                        <InformationTooltip content='Please note, if you select a redundant question the AI will attempt to answer and may incorrectly populate the data map.' />
                    </div>
                </div>
                {amendmentModalBody}
                <div className={styles.buttonWrapper}>
                    <ButtonResize showIconButton={false} onClick={confirm} label='Confirm & Save' disabled={parentDatasetInstanceIsDraft} disabledTooltip={disabledConfirmTooltip} />
                </div>
            </div>
        </DatasetModal>
    );
};
